import { useMemo, useState } from 'react';
import {
  Avatar,
  Checkbox,
  Group,
  Popover,
  Text,
  Tooltip,
  Stack,
  ScrollArea,
} from '@mantine/core';
import { User } from 'react-feather';
import { getAvatarColor, getInitials } from '../../../utils/avatarHelpers';
import classes from './AvatarFilterBar.module.css';

export interface AvatarFilterItem {
  id: string;
  name: string;
  colorKey?: string;
}

export interface AvatarFilterBarProps {
  items: AvatarFilterItem[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  maxVisible?: number;
  size?: number;
  showUnassigned?: boolean;
}

export function AvatarFilterBar({
  items,
  selectedIds,
  onToggle,
  maxVisible = 5,
  size = 32,
  showUnassigned = false,
}: AvatarFilterBarProps) {
  const [popoverOpened, setPopoverOpened] = useState(false);

  const visibleItems = useMemo(() => items.slice(0, maxVisible), [items, maxVisible]);
  const overflowCount = useMemo(() => {
    const extraUsers = Math.max(items.length - maxVisible, 0);
    return extraUsers + (showUnassigned ? 1 : 0);
  }, [items.length, maxVisible, showUnassigned]);

  // Only overflow items for the popover dropdown (items beyond maxVisible + unassigned)
  const overflowPopoverItems = useMemo(() => {
    const result: Array<AvatarFilterItem & { isUnassigned?: boolean }> = items
      .slice(maxVisible)
      .map((item) => ({
        ...item,
        isUnassigned: false,
      }));
    if (showUnassigned) {
      result.push({ id: 'unassigned', name: 'Unassigned', isUnassigned: true });
    }
    return result;
  }, [items, maxVisible, showUnassigned]);

  if (items.length === 0 && !showUnassigned) return null;

  const renderAvatar = (
    item: AvatarFilterItem,
    index: number,
    isUnassigned = false,
  ) => {
    const isSelected = selectedIds.includes(item.id);
    const color = isUnassigned ? '#888888' : getAvatarColor(item.colorKey || item.id);

    return (
      <Tooltip label={item.name} key={item.id} position="bottom" withArrow>
        <Avatar
          size={size}
          radius="xl"
          onClick={() => onToggle(item.id)}
          style={{
            cursor: 'pointer',
            opacity: 1,
            border: isSelected ? '2px solid var(--mantine-color-blue-5)' : '2px solid #FFFFFF',
            transition: 'all 0.15s ease',
            marginLeft: index > 0 ? -8 : 0,
            zIndex: isSelected ? 10 : items.length - index,
            backgroundColor: color,
            fontSize: size * 0.38,
            fontWeight: 600,
          }}
          styles={{ placeholder: { color: '#FFFFFF' } }}
        >
          {isUnassigned ? <User size={size * 0.45} /> : getInitials(item.name)}
        </Avatar>
      </Tooltip>
    );
  };

  return (
    <Group gap={0} align="center">
      {visibleItems.map((item, i) => renderAvatar(item, i))}

      {/* Overflow chip with popover — includes extra users + unassigned */}
      {overflowCount > 0 && (
        <Popover
          opened={popoverOpened}
          onChange={setPopoverOpened}
          position="bottom-start"
          shadow="md"
          withinPortal
        >
          <Popover.Target>
            <Tooltip label={`${overflowCount} more`} position="bottom" withArrow>
              <Avatar
                size={size}
                radius="xl"
                onClick={() => setPopoverOpened((o) => !o)}
                style={{
                  cursor: 'pointer',
                  marginLeft: -8,
                  zIndex: 0,
                  backgroundColor: '#E8E6E1',
                  fontSize: size * 0.38,
                  fontWeight: 600,
                  border: '2px solid #FFFFFF',
                }}
                styles={{ placeholder: { color: '#666666' } }}
              >
                +{overflowCount}
              </Avatar>
            </Tooltip>
          </Popover.Target>

          <Popover.Dropdown p="xs">
            <ScrollArea.Autosize mah={280}>
              <Stack gap={2}>
                {overflowPopoverItems.map((item) => {
                  const isSelected = selectedIds.includes(item.id);
                  const color = item.isUnassigned
                    ? '#888888'
                    : getAvatarColor(item.colorKey || item.id);

                  return (
                    <Group
                      key={item.id}
                      gap="xs"
                      wrap="nowrap"
                      className={classes.dropdownRow}
                      onClick={() => onToggle(item.id)}
                      py={4}
                      px={6}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => onToggle(item.id)}
                        size="xs"
                        styles={{ input: { cursor: 'pointer' } }}
                      />
                      <Avatar
                        size={24}
                        radius="xl"
                        style={{
                          backgroundColor: color,
                          fontSize: 10,
                          fontWeight: 600,
                        }}
                        styles={{ placeholder: { color: '#FFFFFF' } }}
                      >
                        {item.isUnassigned ? (
                          <User size={12} />
                        ) : (
                          getInitials(item.name)
                        )}
                      </Avatar>
                      <Text size="sm" truncate>
                        {item.name}
                      </Text>
                    </Group>
                  );
                })}
              </Stack>
            </ScrollArea.Autosize>
          </Popover.Dropdown>
        </Popover>
      )}
    </Group>
  );
}
